// src/pages/Tarotscope.js
import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/seo";
import { currentLanguage } from "../utils/locale";
import { localizedPath } from "../utils/i18n";

/* ===== Shared style tokens ===== */
const WRAP = "px-[1vw]";
const MAXW = "max-w-10xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";

/* Page canvas (turq/plum ribbons) */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.12), rgba(118, 159, 171, 0) 40%),
  linear-gradient(180deg, #014C75 10%, #005C83 50%, #016280ff 30%, #0C4B84 72%, #014C75 90%)
`;

/* Icon glow recipe */
const ICON_GLOW_BASE =
  "w-24 h-24 md:w-24 md:h-24 rounded-lg object-contain " +
  "shadow-[0_2px_4px_rgba(0,0,0,0.45)] " +
  "transition-transform duration-200 group-hover:-translate-y-[1px] " +
  "pointer-events-none select-none";

const ICON_GLOW_HOV = "group-hover:shadow-[0_16px_36px_rgba(88,64,120,0.55)]";

/* Where your zodiac icons live */
const ICON_BASE = "/icons";

/* Signs with date ranges */
const SIGNS = [
  { name: "Aries", slug: "aries", dates: "Mar 21 - Apr 19" },
  { name: "Taurus", slug: "taurus", dates: "Apr 20 - May 20" },
  { name: "Gemini", slug: "gemini", dates: "May 21 - Jun 20" },
  { name: "Cancer", slug: "cancer", dates: "Jun 21 - Jul 22" },
  { name: "Leo", slug: "leo", dates: "Jul 23 - Aug 22" },
  { name: "Virgo", slug: "virgo", dates: "Aug 23 - Sep 22" },
  { name: "Libra", slug: "libra", dates: "Sep 23 - Oct 22" },
  { name: "Scorpio", slug: "scorpio", dates: "Oct 23 - Nov 21" },
  { name: "Sagittarius", slug: "sagittarius", dates: "Nov 22 - Dec 21" },
  { name: "Capricorn", slug: "capricorn", dates: "Dec 22 - Jan 19" },
  { name: "Aquarius", slug: "aquarius", dates: "Jan 20 - Feb 18" },
  { name: "Pisces", slug: "pisces", dates: "Feb 19 - Mar 20" },
];

const SIGN_ES = {
  aries: { name: "Aries", dates: "21 de marzo - 19 de abril" },
  taurus: { name: "Tauro", dates: "20 de abril - 20 de mayo" },
  gemini: { name: "Geminis", dates: "21 de mayo - 20 de junio" },
  cancer: { name: "Cancer", dates: "21 de junio - 22 de julio" },
  leo: { name: "Leo", dates: "23 de julio - 22 de agosto" },
  virgo: { name: "Virgo", dates: "23 de agosto - 22 de septiembre" },
  libra: { name: "Libra", dates: "23 de septiembre - 22 de octubre" },
  scorpio: { name: "Escorpio", dates: "23 de octubre - 21 de noviembre" },
  sagittarius: { name: "Sagitario", dates: "22 de noviembre - 21 de diciembre" },
  capricorn: { name: "Capricornio", dates: "22 de diciembre - 19 de enero" },
  aquarius: { name: "Acuario", dates: "20 de enero - 18 de febrero" },
  pisces: { name: "Piscis", dates: "19 de febrero - 20 de marzo" },
};

const siteUrl = "https://tarotfate.com";
const ogImage = `${siteUrl}/icons/og-cover.jpg`;
const metaDescription =
  "Pick your zodiac sign for a daily tarotscope that blends astrology with a tarot card theme for reflection and practical guidance.";

function schemaGraphForTarotscope(isSpanish) {
  const localizedPageUrl = `${siteUrl}${localizedPath("/tarotscope")}`;
  const localizedDescription = isSpanish
    ? "Elige tu signo zodiacal para ver un tarotscopo diario que une astrologia, tarot y una guia practica para reflexionar."
    : metaDescription;
  return {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${siteUrl}#org`,
      name: "TarotFate",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/icons/logo.webp`,
        width: 512,
        height: 512,
      },
    },
    {
      "@type": "WebSite",
      "@id": `${siteUrl}#website`,
      url: siteUrl,
      name: "TarotFate",
      publisher: { "@id": `${siteUrl}#org` },
      inLanguage: isSpanish ? "es" : "en",
    },
    {
      "@type": "CollectionPage",
      "@id": `${localizedPageUrl}#webpage`,
      url: localizedPageUrl,
      name: isSpanish ? "Tarotscopo diario | TarotFate" : "Daily Tarotscope | TarotFate",
      description: localizedDescription,
      isPartOf: { "@id": `${siteUrl}#website` },
      primaryImageOfPage: { "@id": `${localizedPageUrl}#image` },
      breadcrumb: { "@id": `${localizedPageUrl}#breadcrumbs` },
      mainEntity: { "@id": `${localizedPageUrl}#signs` },
      inLanguage: isSpanish ? "es" : "en",
    },
    {
      "@type": "ImageObject",
      "@id": `${localizedPageUrl}#image`,
      url: ogImage,
      width: 1200,
      height: 630,
      caption: "TarotFate cover",
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${localizedPageUrl}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: isSpanish ? "Inicio" : "Home", item: isSpanish ? `${siteUrl}/es` : siteUrl },
        { "@type": "ListItem", position: 2, name: isSpanish ? "Tarotscopo" : "Tarotscope", item: localizedPageUrl },
      ],
    },
    {
      "@type": "ItemList",
      "@id": `${localizedPageUrl}#signs`,
      name: isSpanish ? "Signos del tarotscopo" : "Zodiac tarotscope signs",
      itemListElement: SIGNS.map((sign, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: isSpanish ? `${SIGN_ES[sign.slug]?.name || sign.name} Tarotscopo` : `${sign.name} Tarotscope`,
        url: `${siteUrl}${localizedPath(`/tarotscope/${sign.slug}`)}`,
      })),
    },
  ],
};
}

export default function Tarotscope() {
  const isSpanish = currentLanguage() === "es";
  const copy = isSpanish
    ? {
        title: "Tarotscopo diario | TarotFate",
        description:
          "Elige tu signo zodiacal para ver un tarotscopo diario que une astrologia, tarot y una guia practica para reflexionar.",
        intro:
          "Elige tu signo para ver el mensaje de hoy. La astrologia marca el tono y el momento. Una carta de tarot agrega un tema claro para usar ahora: que abrazar, que suavizar y donde un paso valiente puede cambiar tu dia.",
        selectLabel: "Selecciona tu signo zodiacal",
        iconAlt: "icono de tarotscopo",
      }
    : {
        title: "Daily Tarotscope | TarotFate",
        description: metaDescription,
        intro:
          "Pick your sign to view today's message. Astrology frames the mood and timing. A Tarot card adds a clear theme you can use now. Notice what to lean into, what to soften and where one brave step could shift your day.",
        selectLabel: "Select your zodiac sign",
        iconAlt: "tarotscope icon",
      };

  return (
    <div
      className={`${FONT} min-h-screen relative overflow-hidden text-white`}
      style={{ background: CANVAS_BG }}
    >
      <Seo
        title={copy.title}
        description={copy.description}
        url={`${siteUrl}${localizedPath("/tarotscope")}`}
        image={ogImage}
        schema={schemaGraphForTarotscope(isSpanish)}
      />

      {/* ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* Header */}
      <header className={`${WRAP} ${MAXW} pt-8 pb-4 text-center relative z-[1]`}>
        <div className="flex justify-center">
          <img
            src="/icons/tarotscope.webp"
            alt={isSpanish ? "Tarotscopo" : "Tarotscope"}
            className="w-32 h-auto sm:w-36 md:w-40 lg:w-44 object-contain mx-auto select-none pointer-events-none"
            onContextMenu={(e) => e.preventDefault()}
            draggable="false"
          />
        </div>
        <div className="flex items-center justify-center mt-2 mb-2" aria-hidden>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          <span className="px-4 text-white/90">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
        <p className={`${TEXT} max-w-3xl mx-auto leading-relaxed text-base`}>
          {copy.intro}
        </p>
      </header>

      {/* Grid */}
      <main className={`${WRAP} ${MAXW} pb-16 relative z-[1]`}>
        <div
          className="
            grid gap-4
            grid-cols-2
            md:grid-cols-4
            xl:grid-cols-4
          "
          role="list"
          aria-label={copy.selectLabel}
        >
          {SIGNS.map((s) => {
            const label = isSpanish ? SIGN_ES[s.slug]?.name || s.name : s.name;
            const dates = isSpanish ? SIGN_ES[s.slug]?.dates || s.dates : s.dates;
            const img = `${ICON_BASE}/tarotscope_${s.slug}.webp`;
            const alt = `${label} ${copy.iconAlt}`;

            return (
              <Link
                to={localizedPath(`/tarotscope/${s.slug}`)}
                key={s.slug}
                className="
                  group rounded-none select-none focus:outline-none
                  focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f3d3f]
                  transition-all duration-150 hover:-translate-y-1
                  text-[#3f2d61] backdrop-blur-md
                  bg-gradient-to-b from-white/3 via-[#14656a]/35 to-[#0f3d3f]/12
                  [background-clip:padding-box,border-box]
                  shadow-[0_0px_0px_0_rgba(0,0,0,0.8)]
                  p-4 text-center
                "
                role="listitem"
                aria-label={isSpanish ? `${label} tarotscopo` : `${label} tarotscope`}
              >
                {/* Icon + glow */}
                <div className="flex justify-center pt-1">
                  <div className="relative">
                    <img
                      src={img}
                      alt={alt}
                      width={96}
                      height={96}
                      loading="lazy"
                      decoding="async"
                      draggable="false"
                      className={`${ICON_GLOW_BASE} ${ICON_GLOW_HOV}`}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    {/* hover bloom */}
                    <span
                      aria-hidden
                      className="
                        absolute inset-0 rounded-lg opacity-0
                        group-hover:opacity-100 transition duration-200 ease-out
                        blur-[16px]
                        bg-[radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,0.55),transparent_70%)]
                        pointer-events-none
                      "
                    />
                  </div>
                </div>

                {/* Labels */}
                <div className="flex flex-col items-center text-center px-3 pb-1 mt-2">
                  <h3 className="text-base font-semibold text-white tracking-wide">
                    {label}
                  </h3>
                  <p className="mt-1 text-[14px] text-white/80 leading-snug">
                    {dates}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

       
         
      </main>
    </div>
  );
}
